// Interim data scales, in one place until design system v1's data-scale tokens land in
// design/system/ (#2, 5932358283). The names are design's: velocity (sequential warm), pressure
// (diverging cool to warm), perfusion, tissue fate, activity (ember), receptor occupancy (teal).
// The viewport gets the same stops, so a trace, a legend and the 3D picture agree.

import type { Colormap } from '../viewport/api.ts';
import { tokens } from '../../../../design/system/tokens.ts';

export type Stops = [number, string][];

export const RAMPS = {
  velocity: [
    [0, '#2a0f24'],
    [0.25, '#7d1d3f'],
    [0.5, '#d9434e'],
    [0.75, '#ff9a4d'],
    [1, '#ffe7a6'],
  ],
  pressure: [
    [0, '#2f63d1'],
    [0.25, '#7eaef2'],
    [0.5, '#e6e8ee'],
    [0.75, '#f29b7b'],
    [1, '#d2433a'],
  ],
  perfusion: [
    [0, '#170a33'],
    [0.35, '#55288a'],
    [0.6, '#ad4f9f'],
    [0.82, '#f08b78'],
    [1, '#fde4a8'],
  ],
  // Starts at quiet tissue (a cool slate), not black: activity reads as embers on the brain.
  activity: [
    [0, '#3a404c'],
    [0.3, '#6e2614'],
    [0.55, '#d0461a'],
    [0.8, '#ff9d3f'],
    [1, '#fff1c4'],
  ],
  occupancy: [
    [0, '#05222a'],
    [0.3, '#0c5862'],
    [0.6, '#16a19f'],
    [0.85, '#5fe0c8'],
    [1, '#dafff4'],
  ],
  // Design system v1's scale.sd: cold, so a wave reads apart from seizure's ember and the deficit's amber it crosses.
  sd: tokens.scale.sd.$value.map((x) => [x.position, x.color] as [number, string]),
} satisfies Record<string, Stops>;

export type RampName = keyof typeof RAMPS;

/** Tissue fate, categorical (hemo-sim-v0 labels; oligaemia is design's fifth class): design's
 *  scale.tissue-fate, the colours the Tissue section's swatches take too. Healthy has no colour in 3D
 *  (the tissue keeps its own); the chart draws it in slate. */
const FATE = tokens.scale['tissue-fate'].$value;
export const TISSUE_FATE: Record<string, string> = {
  healthy: '#6b7787',
  oligaemia: FATE.oligaemia,
  penumbra: FATE.penumbra,
  core: FATE.core,
  'reperfused-saved': FATE.reperfused,
};

/** Trace lines, in order; distinct under the common colour-vision deficiencies at line weight. */
export const SERIES = ['#7cc4ff', '#ff9a4d', '#5fe0c8', '#e58cff', '#f5cf4a', '#ff6b6b', '#a7e36b', '#c3b5ff'];

/** Design system v1's trace colours (`scale.trace`): the plain line, and spike and onset markers. */
export const TRACE = { line: tokens.scale.trace.line.$value, event: tokens.scale.trace.event.$value };

/** A viewport colormap from a ramp, so the engine draws what the app's legends show. */
export function colormap(name: RampName): Colormap {
  return { stops: RAMPS[name].map(([t, c]) => [t, c] as [number, string]) };
}

const rgbCache = new Map<string, [number, number, number]>();
export function hexRgb(hex: string): [number, number, number] {
  let c = rgbCache.get(hex);
  if (!c) {
    const n = Number.parseInt(hex.replace('#', ''), 16);
    c = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    rgbCache.set(hex, c);
  }
  return c;
}

/** The colour at t (0–1, clamped) along stops, as [r, g, b] 0–255. */
export function sampleStops(stops: Stops, t: number, out: [number, number, number] = [0, 0, 0]): [number, number, number] {
  const x = Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0;
  let k = 1;
  while (k < stops.length - 1 && stops[k]![0] < x) k++;
  const [t0, c0] = stops[k - 1]!;
  const [t1, c1] = stops[k] ?? stops[k - 1]!;
  const w = t1 > t0 ? Math.min(1, Math.max(0, (x - t0) / (t1 - t0))) : 0;
  const a = hexRgb(c0);
  const b = hexRgb(c1);
  out[0] = Math.round(a[0] + (b[0] - a[0]) * w);
  out[1] = Math.round(a[1] + (b[1] - a[1]) * w);
  out[2] = Math.round(a[2] + (b[2] - a[2]) * w);
  return out;
}

/** Stops for any viewport colormap: named ones by their standard anchors, custom ones as given. */
export function colormapStops(c: Colormap): Stops {
  if (typeof c !== 'string') return c.stops;
  return NAMED[c];
}

const NAMED: Record<Exclude<Colormap, { stops: unknown }>, Stops> = {
  viridis: [
    [0, '#440154'],
    [0.25, '#3b528b'],
    [0.5, '#21918c'],
    [0.75, '#5ec962'],
    [1, '#fde725'],
  ],
  magma: [
    [0, '#000004'],
    [0.25, '#51127c'],
    [0.5, '#b73779'],
    [0.75, '#fc8961'],
    [1, '#fcfdbf'],
  ],
  inferno: [
    [0, '#000004'],
    [0.25, '#57106e'],
    [0.5, '#bc3754'],
    [0.75, '#f98e09'],
    [1, '#fcffa4'],
  ],
  turbo: [
    [0, '#30123b'],
    [0.25, '#28bceb'],
    [0.5, '#a4fc3c'],
    [0.75, '#fb7e21'],
    [1, '#7a0403'],
  ],
  coolwarm: [
    [0, '#3b4cc0'],
    [0.5, '#dddddd'],
    [1, '#b40426'],
  ],
};

/** A CSS linear-gradient for a legend bar. */
export function gradientCss(stops: Stops, direction = 'to right'): string {
  return `linear-gradient(${direction}, ${stops.map(([t, c]) => `${c} ${(t * 100).toFixed(1)}%`).join(', ')})`;
}
